import { initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import apiClient, { jsonRequestConfig } from "../api/httpClient";
import endpoints from "../api/endpoints";

const FCM_TOKEN_STORAGE_KEY = "iot-web-fcm-token";
const FCM_TOKEN_USER_STORAGE_KEY = "iot-web-fcm-token-user";
const publicUrl = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");

const firebaseConfig = {
  apiKey: "AIzaSyCRQnf0oeV8FZyiMicBzTKCyiXYZREZhYU",
  authDomain: "sama-iot.firebaseapp.com",
  projectId: "sama-iot",
  storageBucket: "sama-iot.firebasestorage.app",
  messagingSenderId: "28734367175",
  appId: "1:28734367175:web:f79cedaec375bcd3bea5e2",
  measurementId: "G-76F2W5M41F"
};

let firebaseApp = null;
let messagingInstance = null;
let supportPromise = null;

const hasFirebaseConfig = () =>
  Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId
  );

const getVapidKey = () => String(process.env.REACT_APP_FIREBASE_VAPID_KEY || "").trim();

export const getCachedWebFcmToken = () => localStorage.getItem(FCM_TOKEN_STORAGE_KEY) || "";

export const isFirebaseMessagingConfigured = () => hasFirebaseConfig() && Boolean(getVapidKey());

export const canUseFirebaseMessaging = async () => {
  if (!hasFirebaseConfig() || !getVapidKey()) return false;
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  if (!("serviceWorker" in navigator)) return false;

  if (!supportPromise) {
    supportPromise = isSupported().catch(() => false);
  }

  return supportPromise;
};

const getFirebaseMessaging = async () => {
  const supported = await canUseFirebaseMessaging();
  if (!supported) return null;

  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig);
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(firebaseApp);
  }

  return messagingInstance;
};

const registerMessagingServiceWorker = async () => {
  const scope = `${publicUrl}/firebase-cloud-messaging-push-scope`;
  const scriptUrl = `${publicUrl}/firebase-messaging-sw.js`;
  const existingRegistration = await navigator.serviceWorker.getRegistration(scope);
  const registration =
    existingRegistration ||
    (await navigator.serviceWorker.register(scriptUrl, { scope }));

  if (existingRegistration) {
    try {
      await registration.update();
    } catch (error) {
      console.warn("[FCM] Failed to update messaging service worker:", error);
    }
  }

  const worker = registration.installing || registration.waiting;
  if (!worker && registration.active) return registration;
  if (!worker) {
    return navigator.serviceWorker.ready;
  }

  await new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Firebase messaging service worker did not activate in time."));
    }, 10000);

    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        window.clearTimeout(timeoutId);
        resolve();
      }
    });
  });

  return registration;
};

export const requestAndSaveWebFcmToken = async ({ userId, forcePermissionPrompt = false } = {}) => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return { status: "unsupported" };

  if (Notification.permission === "denied") {
    return { status: "denied" };
  }

  if (Notification.permission !== "granted") {
    if (!forcePermissionPrompt) return { status: "needs-permission" };

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { status: permission || "denied" };
  }

  const serviceWorkerRegistration = await registerMessagingServiceWorker();
  const token = await getToken(messaging, {
    vapidKey: getVapidKey(),
    serviceWorkerRegistration,
  });

  if (!token) return { status: "empty-token" };

  const cachedToken = getCachedWebFcmToken();
  const cachedUser = localStorage.getItem(FCM_TOKEN_USER_STORAGE_KEY);
  const normalizedUserId = userId ? String(userId) : "";

  if (token !== cachedToken || normalizedUserId !== cachedUser) {
    await apiClient.post(
      endpoints.user.fcmToken(),
      { fcmToken: token, platform: "web" },
      jsonRequestConfig
    );
    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    localStorage.setItem(FCM_TOKEN_USER_STORAGE_KEY, normalizedUserId);
  }

  return { status: "ready", token };
};

export const removeWebFcmToken = async () => {
  const token = getCachedWebFcmToken();

  try {
    const messaging = await getFirebaseMessaging();
    if (messaging && token) {
      await deleteToken(messaging);
    }
  } catch (error) {
    console.warn("[FCM] Failed to delete browser token:", error);
  } finally {
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    localStorage.removeItem(FCM_TOKEN_USER_STORAGE_KEY);
  }

  return token;
};

export const subscribeToForegroundMessages = async (handler) => {
  
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    handler(payload);
  });
};
